/* eslint-disable complexity */
import React, { CSSProperties, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import clsx from 'clsx';
import { deburr } from 'lodash';

import { getTimestampRelativeFormat } from '@futrli/localisation';

import Search, { SearchEvent } from 'carbon-react/lib/components/search';
import Icon from 'carbon-react/lib/components/icon';

import { LinkedSheetData } from '../../types';

import styles from './linked-template-list.scss';

// import { mockedData } from './mock-data';

interface LinkedTemplateListProps {
  className?: string;
  data: LinkedSheetData[];
  onLoginClick?: (item: LinkedSheetData) => void;
  onOpenClick?: (item: LinkedSheetData) => void;
  style?: CSSProperties;
}

export const LinkedTemplateList = ({ className, data, onLoginClick, onOpenClick, style }: LinkedTemplateListProps) => {
  const intl = useIntl();

  const [searchCriteria, setSearchCriteria] = useState('');

  const filteredItems = useMemo(() => {
    if (!searchCriteria) {
      return data;
    }
    return data.filter(
      item =>
        deburr(item.entity?.name || '')
          .toLowerCase()
          .indexOf(searchCriteria) !== -1 ||
        deburr(item.sheetInfo?.data?.import_sheet_info.sheet_name || '')
          .toLowerCase()
          .indexOf(searchCriteria) !== -1
    );
  }, [data, searchCriteria]);

  const handleSearchChange = useCallback((event: SearchEvent) => {
    setSearchCriteria(event.target.value.toLowerCase());
  }, []);

  const getRowContent = (item: LinkedSheetData) => {
    const isLoading = item.sheetInfoLoading || item.tokenLoading;
    const isError = !isLoading && (item.sheetInfo?.error || item.token?.error);
    const isLoggedOut = item.token?.hasToken === false;

    if (isLoading) {
      return (
        <td colSpan={2}>
          <Icon type="sync" />
        </td>
      );
    }
    if (isError) {
      return (
        <td colSpan={2}>
          <FormattedMessage id="import.error_loading_data" defaultMessage="Could not load sheet data" />
        </td>
      );
    }
    if (isLoggedOut) {
      return (
        <td colSpan={2}>
          <a
            href={item.token?.authUrl}
            target="_blank"
            rel="noreferrer"
            onClick={onLoginClick ? () => onLoginClick?.(item) : undefined}
          >
            <FormattedMessage id="common.login" defaultMessage="Login" />
          </a>
        </td>
      );
    }
    return (
      <>
        <td>
          {item.sheetInfo?.data?.import_sheet_info.sheet_name}
          <br />
          <span className={styles.small}>
            <FormattedMessage
              id="predict.import.open"
              defaultMessage="Last synced: {lastSynced}"
              values={{
                lastSynced: item.lastSyncedAt ? (
                  getTimestampRelativeFormat(item.lastSyncedAt, Date.now(), 1, false, { sentenceCase: true })
                ) : (
                  <FormattedMessage id="common.never" defaultMessage="Never" />
                )
              }}
            />
          </span>
        </td>
        <td>
          <a
            href={item.sheetInfo?.data?.import_sheet_info.sheet_url}
            target="_blank"
            rel="noreferrer"
            onClick={onOpenClick ? () => onOpenClick?.(item) : undefined}
          >
            <FormattedMessage id="common.open" defaultMessage="Open" />
          </a>
        </td>
      </>
    );
  };

  return (
    <div className={clsx(styles.container, className)} style={style} data-testid="linked-template-list">
      <p className={styles.intro}>
        <FormattedMessage id="import.linked_sheets_intro_1" defaultMessage="Import templates linked to this forecast" />
        <span className={styles.intro2}>
          <FormattedMessage id="import.linked_sheets_intro_2" defaultMessage="Open to edit values and sync above." />
        </span>
      </p>

      {data.length ? (
        <>
          <Search
            value={searchCriteria}
            onChange={handleSearchChange}
            label={intl.formatMessage({
              id: 'common.search_ellipsis',
              defaultMessage: 'Search...'
            })}
          />
          <table className={styles.table}>
            <tbody>
              {filteredItems.map(item => (
                <tr key={item.sheetId}>
                  <td>
                    <strong>{item.entity?.name}</strong>
                  </td>
                  {getRowContent(item)}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <p>
          <FormattedMessage
            id="import.no_linked_sheets_available"
            defaultMessage="There are no google sheets linked with any of the sub-forecasts in this consolidated forecast."
          />
        </p>
      )}
    </div>
  );
};
